/**
 * Shared patch builders for the library.
 *
 * These exist because the interesting part of a drum patch is three or four numbers, and
 * writing the other thirty out a hundred times would bury them. Each builder covers one
 * physical idea — a transient, a tuned body, a filtered noise burst, a tuned two-partial
 * tone — and every sound is those ideas stacked and then adjusted.
 *
 * Every default here is the default the native `LibraryPatch` uses. They are not cosmetic:
 * a transient's 0.85 curve is what makes it a click rather than a blip, and the 7.03
 * semitone gap between the two metal squares is what stops a hi-hat sounding like a chord.
 */

import {
  makeEffectChain,
  makeEffectNode,
  normaliseEffectNode,
  type EffectChain,
  type EffectKind,
  type EffectNode,
} from '../../model/types';
import {
  makeSynthEnvelope,
  makeSynthFilter,
  makeSynthLayer,
  makeSynthLFO,
  makeSynthPatch,
  makeSynthPitchEnvelope,
  type SynthEnvelope,
  type SynthFilter,
  type SynthLayer,
  type SynthLFO,
  type SynthPatch,
  type SynthPitchEnvelope,
  type SynthWaveform,
} from '../synth';
import { librarySeed, libraryUUID } from './identity';

/** Duration that exactly contains an envelope, so nothing renders trailing silence. */
export function envelopeDuration(envelope: SynthEnvelope): number {
  const bursts = Math.max(envelope.burstCount - 1, 0) * envelope.burstInterval;
  return envelope.delay + envelope.attack + envelope.hold + envelope.decay + envelope.release + bursts;
}

export interface LibraryEffect {
  kind: EffectKind;
  values: Record<string, number>;
}

export function effect(kind: EffectKind, values: Record<string, number>): LibraryEffect {
  return { kind, values };
}

function stampedNodes(scope: number, slug: string, slotBase: number, nodes: readonly LibraryEffect[]): EffectNode[] {
  return nodes.map((entry, index) => {
    const node = makeEffectNode(entry.kind, {
      id: libraryUUID(scope, slug, slotBase + index),
      values: { ...entry.values },
    });
    // Fills in every parameter the recipe left out and clamps the ones it set, so a node
    // built from four numbers behaves exactly like one built from the full table.
    normaliseEffectNode(node);
    return node;
  });
}

/**
 * Stamps a post chain's node identifiers from the slug, for the same reason the patches are
 * stamped: two calls have to produce two equal recipes.
 */
export function libraryChain(slug: string, nodes: readonly LibraryEffect[]): EffectChain {
  return makeEffectChain(stampedNodes(0x04, slug, 0, nodes));
}

export interface LibraryPatchOptions {
  slug: string;
  slot: number;
  layers: SynthLayer[];
  amplitude: SynthEnvelope;
  filter?: SynthFilter;
  pitchEnvelope?: SynthPitchEnvelope;
  lfo?: SynthLFO;
  baseFrequency: number;
  duration?: number;
  drive?: number;
  chain?: readonly LibraryEffect[];
}

export function libraryPatch(options: LibraryPatchOptions): SynthPatch {
  const amplitude = options.amplitude;
  return makeSynthPatch({
    name: options.slug,
    symbolName: 'waveform',
    layers: options.layers,
    amplitude,
    filter: options.filter ?? makeSynthFilter(),
    pitchEnvelope: options.pitchEnvelope ?? makeSynthPitchEnvelope({ amountSemitones: 0, decay: 0.05, curve: 0 }),
    lfo: options.lfo ?? makeSynthLFO(),
    baseFrequency: options.baseFrequency,
    duration: options.duration ?? envelopeDuration(amplitude),
    drive: options.drive ?? 0,
    chain: makeEffectChain(stampedNodes(0x03, options.slug, options.slot * 8, options.chain ?? [])),
    tempo: 120,
    seed: librarySeed(options.slug, options.slot),
  });
}

export interface TransientOptions {
  slug: string;
  slot: number;
  highPass: number;
  decay: number;
  toneHz?: number;
  toneLevel?: number;
  resonance?: number;
  drive?: number;
}

/**
 * The click. A few milliseconds of high-passed noise with a little tone in it — the beater,
 * the stick, the snap. Always its own layer, because it has to be over before the body has
 * finished its attack.
 */
export function transientPatch(options: TransientOptions): SynthPatch {
  const toneLevel = options.toneLevel ?? 0;
  const layers = [makeSynthLayer({ waveform: 'noise', level: 1 })];
  if (toneLevel > 0) layers.push(makeSynthLayer({ waveform: 'triangle', level: toneLevel }));

  return libraryPatch({
    slug: options.slug,
    slot: options.slot,
    layers,
    amplitude: makeSynthEnvelope({
      attack: 0.0003,
      hold: 0.0005,
      decay: options.decay,
      sustain: 0,
      release: 0.004,
      curve: 0.85,
    }),
    filter: makeSynthFilter({
      type: 'highPass',
      cutoff: options.highPass,
      resonance: options.resonance ?? 0.8,
    }),
    baseFrequency: toneLevel > 0 ? (options.toneHz ?? 0) : 1000,
    drive: options.drive ?? 0.15,
  });
}

export interface BodyOptions {
  slug: string;
  slot: number;
  frequency: number;
  dropSemitones: number;
  dropSeconds: number;
  dropCurve?: number;
  decay: number;
  hold?: number;
  attack?: number;
  release?: number;
  curve?: number;
  lowPass?: number;
  lowPassOctaves?: number;
  lowPassDecay?: number;
  resonance?: number;
  harmonic?: { waveform: SynthWaveform; semitones: number; level: number };
  noiseLevel?: number;
  drive?: number;
}

/**
 * The tuned body of a kick, a tom, or an 808: a sine whose pitch falls into place in the
 * first few tens of milliseconds. The pitch envelope is the sound — a sine with a flat pitch
 * is a test tone, and no amount of filtering turns it into a drum.
 */
export function bodyPatch(options: BodyOptions): SynthPatch {
  const layers = [makeSynthLayer({ waveform: 'sine', level: 1, resetsPhase: true, startPhase: 0 })];
  if (options.harmonic !== undefined) {
    layers.push(
      makeSynthLayer({
        waveform: options.harmonic.waveform,
        semitones: options.harmonic.semitones,
        level: options.harmonic.level,
      }),
    );
  }
  const noiseLevel = options.noiseLevel ?? 0;
  if (noiseLevel > 0) layers.push(makeSynthLayer({ waveform: 'noise', level: noiseLevel }));

  const lowPass = options.lowPass ?? 0;
  const filter =
    lowPass > 0
      ? makeSynthFilter({
          type: 'lowPass',
          cutoff: lowPass,
          resonance: options.resonance ?? 1.0,
          envelopeAmount: options.lowPassOctaves ?? 0,
          envelope: makeSynthEnvelope({
            attack: 0.0006,
            decay: options.lowPassDecay ?? 0.03,
            sustain: 0,
            release: 0.01,
            curve: 0.85,
          }),
        })
      : makeSynthFilter();

  return libraryPatch({
    slug: options.slug,
    slot: options.slot,
    layers,
    amplitude: makeSynthEnvelope({
      attack: options.attack ?? 0.0015,
      hold: options.hold ?? 0.008,
      decay: options.decay,
      sustain: 0,
      release: options.release ?? 0.03,
      curve: options.curve ?? 0.55,
    }),
    filter,
    pitchEnvelope: makeSynthPitchEnvelope({
      amountSemitones: options.dropSemitones,
      decay: options.dropSeconds,
      curve: options.dropCurve ?? 0.8,
    }),
    baseFrequency: options.frequency,
    drive: options.drive ?? 0.25,
  });
}

export interface NoiseOptions {
  slug: string;
  slot: number;
  filterType: SynthFilter['type'];
  cutoff: number;
  resonance?: number;
  envelopeOctaves?: number;
  envelopeDecay?: number;
  attack?: number;
  hold?: number;
  decay: number;
  sustain?: number;
  release?: number;
  curve?: number;
  burstCount?: number;
  burstInterval?: number;
  metalLevel?: number;
  metalBase?: number;
  drive?: number;
}

/**
 * Filtered noise with an optional pair of detuned squares underneath it. That pair is what
 * separates a hi-hat from a hiss: real cymbal metal is inharmonic, and two squares a fifth
 * apart through a high-pass is the cheapest convincing version of it.
 */
export function noisePatch(options: NoiseOptions): SynthPatch {
  const metalLevel = options.metalLevel ?? 0;
  const layers = [makeSynthLayer({ waveform: 'noise', level: 1 })];
  if (metalLevel > 0) {
    layers.push(makeSynthLayer({ waveform: 'square', level: metalLevel }));
    layers.push(makeSynthLayer({ waveform: 'square', semitones: 7.03, level: metalLevel * 0.72 }));
  }
  const metalBase = options.metalBase ?? 6400;

  return libraryPatch({
    slug: options.slug,
    slot: options.slot,
    layers,
    amplitude: makeSynthEnvelope({
      attack: options.attack ?? 0.0004,
      hold: options.hold ?? 0.002,
      decay: options.decay,
      sustain: options.sustain ?? 0,
      release: options.release ?? 0.012,
      curve: options.curve ?? 0.72,
      burstCount: options.burstCount ?? 1,
      burstInterval: options.burstInterval ?? 0.008,
    }),
    filter: makeSynthFilter({
      type: options.filterType,
      cutoff: options.cutoff,
      resonance: options.resonance ?? 0.8,
      envelopeAmount: options.envelopeOctaves ?? 0,
      envelope: makeSynthEnvelope({
        attack: 0.0004,
        decay: options.envelopeDecay ?? 0.03,
        sustain: 0,
        release: 0.02,
        curve: 0.8,
      }),
    }),
    baseFrequency: metalLevel > 0 ? metalBase : options.cutoff,
    drive: options.drive ?? 0.12,
  });
}

export interface ToneOptions {
  slug: string;
  slot: number;
  waveform: SynthWaveform;
  frequency: number;
  partial?: { semitones: number; level: number };
  attack?: number;
  hold?: number;
  decay: number;
  sustain?: number;
  release?: number;
  curve?: number;
  filter?: SynthFilter;
  pitchEnvelope?: SynthPitchEnvelope;
  noiseLevel?: number;
  drive?: number;
}

/**
 * A tuned two-partial tone: cowbells, woodblocks, rims, triangles, and the pitched half of a
 * snare.
 */
export function tonePatch(options: ToneOptions): SynthPatch {
  const layers = [makeSynthLayer({ waveform: options.waveform, level: 1 })];
  if (options.partial !== undefined) {
    layers.push(
      makeSynthLayer({
        waveform: options.waveform,
        semitones: options.partial.semitones,
        level: options.partial.level,
      }),
    );
  }
  const noiseLevel = options.noiseLevel ?? 0;
  if (noiseLevel > 0) layers.push(makeSynthLayer({ waveform: 'noise', level: noiseLevel }));

  return libraryPatch({
    slug: options.slug,
    slot: options.slot,
    layers,
    amplitude: makeSynthEnvelope({
      attack: options.attack ?? 0.0006,
      hold: options.hold ?? 0.002,
      decay: options.decay,
      sustain: options.sustain ?? 0,
      release: options.release ?? 0.02,
      curve: options.curve ?? 0.7,
    }),
    filter: options.filter ?? makeSynthFilter(),
    pitchEnvelope: options.pitchEnvelope ?? makeSynthPitchEnvelope({ amountSemitones: 0, decay: 0.05, curve: 0 }),
    baseFrequency: options.frequency,
    drive: options.drive ?? 0.15,
  });
}
